import { Grid, Tooltip, Theme } from "@mui/material";
import React, { useCallback, useMemo, useState, useEffect } from "react";

import WarningIcon from "@mui/icons-material/Warning";
import { FlightRemovalType, IFlight, IPositioningFlight } from "@vmosolutions/oc-interfaces";
import classNames from "classnames";
import { useDrag } from "react-dnd";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import { v1 } from "uuid";
import { convertPosFlightToFlight } from "../CrewSchedule/PairingEditorWindow/ConvertFlights";
import { ChipContextMenu } from "../Flights/ChipContextMenu";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

export type IPosFlightChip = Partial<IPositioningFlight>;

interface IPosFlightChipProps {
  data: IPositioningFlight;
  className?: string;
  onClickChip?: (flightId: string) => void;
  disableContext?: boolean;
  disableFocus?: boolean;
  disableDrag?: boolean;
}

export const PosFlightChip = (props: IPosFlightChipProps): JSX.Element => {
  const { data, className, onClickChip, disableContext } = props;
  const [contextAnchor, setContextAnchor] = useState(document.createElement("div"));
  const [internalFlightData, setInternalFlightData] = useState<IFlight>();
  const isICAO = useSelector((state: IRootState) => state.authentication.appSettings?.blockInfo?.icao);
  //!! Watch out because only a limited number of airports are stored in the redux store.
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const [originCode, setOriginCode] = useState(data?.origin);
  const [destCode, setDestCode] = useState(data?.destination);
  const flightRemovalType: FlightRemovalType | undefined = useMemo(() => {
    return data?.removalType;
  }, [data?.removalType]);
  const internalFlight = data?.flight_id;
  const dragType = flightRemovalType ?? "POSFLIGHT";
  const [collected, drag, dragPreview] = useDrag(
    {
      type: dragType,
      item: { flight: props?.data, type: dragType },
    },
    [dragType, data]
  );

  useEffect(() => {
    if (isICAO) {
      setOriginCode(data?.origin);
      setDestCode(data?.destination);
    } else {
      setOriginCode(airportOptions[data?.origin]?.iata ?? "");
      setDestCode(airportOptions[data?.destination]?.iata ?? "");
    }
  }, [airportOptions, data?.destination, data?.origin, isICAO]);

  // Set context menu anchor (opens it);
  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      const getInternalFlight = async (): Promise<void> => {
        const flight = await convertPosFlightToFlight(data);
        if (flight) {
          setInternalFlightData(flight);
        }
      };

      event.preventDefault();
      event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
      setContextAnchor(event.currentTarget);
      //only get the data if you right click on an internal flight
      if (internalFlight && data._id && !data._id.includes("temp") && !internalFlightData) {
        getInternalFlight();
      }
    },
    [data, internalFlight, internalFlightData]
  );

  if (data) {
    const { _id, origin, destination, airlineIATA, flightNumber } = data;
    const airlineFlight = `${airlineIATA ? airlineIATA : ""}${flightNumber}`;
    const color = "ff00ff";
    const posFlightId = _id ? _id : v1();
    // Event has properly formatted ID
    const assignedEvent: boolean = _id && !_id.includes("temp");

    if (!airlineFlight || !color) {
      return <></>;
    }

    // If all required data is passed, render the flight chip.
    const chipClass = posFlightId + "-posflightChip";
    const frColor = color ? (assignedEvent ? invertHexColor(color) : `#${color}`) : "white";
    const bgColor = color ? (assignedEvent ? `#${color}` : "transparent") : "black";
    const borderStyle = color ? (assignedEvent ? "none" : `1px solid #${color}`) : "1px solid white";

    const PairingStyles = (theme: Theme): any => ({
      ContextMenuChip: {
        userSelect: "none",
        width: 120,
        height: 26,
        boxShadow: "2px 2px 5px " + theme.palette.common.black,
        justifyContent: "space-between",
        display: "flex",
        flexDirection: "row",
        margin: "2px",
        borderRadius: "2px",
        fontFamily: "B612 Mono, arial, sans-serif", // Cousine showed poor anti-aliasing (blurred)
        fontSize: 10,
        fontWeight: "normal",
        padding: "5px",
        alignItems: "center",
        color: theme.palette.primary.contrastText, // This get's overriden by the inline style applied to assign best contrast color.
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shortest,
        }),
        "&.cancelled": {
          opacity: 0.75,
          userSelect: "none",
          // pointerEvents: "none", //! this is needed for tooltips to work.
          "& .cancelled_icon": {
            marginLeft: "-5px",
          },
        },
        "& .info": {
          // position: "absolute",
          width: 110,
          "& .flightnumber, & .event_title": {
            fontSize: 12,
            fontWeight: "bold",
            padding: "0px 2px",
          },
          "& .origin, & .destination ": {
            padding: "0px 2px",
          },
        },
        "&.draggable": {
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        },
        "&.posflightchip div": { padding: 0 },
        "&.nonflightchip div": {
          padding: 0,
          "&.title": { fontSize: 12 },
        },
        "& div": {
          display: "flex",
          flexDirection: "row",
          padding: "0px 5px",
        },
        "& .details": {
          flexDirection: "column",
          "& div": { lineHeight: "9px", padding: 0 },
        },
        "& .flightnumber, & .event_title, & .airline_flight": {
          fontSize: 12,
          fontWeight: "bold",
        },
        "&.interactive": {
          "&:hover": { margin: "2px 0px", width: 124 },
          "&:active": { margin: "2px 4px", width: 116 },
        },
      },
    });

    return (
      <Tooltip
        disableFocusListener={true}
        disableTouchListener={true}
        disableHoverListener={!flightRemovalType} // If flight isn't cancelled, disabled tooltip completely.
        title={
          flightRemovalType
            ? `Flight has been ${flightRemovalType === FlightRemovalType.CancelFlight ? "canceled" : "deleted"}`
            : ""
        }
        placement="top"
      >
        <Grid
          ref={_id && !flightRemovalType ? drag : null}
          item
          id={posFlightId}
          key={posFlightId}
          style={{
            color: frColor,
            backgroundColor: bgColor,
            border: borderStyle,
          }}
          sx={(theme) => PairingStyles(theme).ContextMenuChip}
          className={classNames(
            className,
            "posflightchip",
            !!onClickChip && !!_id && "interactive",
            chipClass,
            flightRemovalType && "cancelled"
          )}
          onClick={() => !!onClickChip && !!_id && !flightRemovalType && onClickChip(_id)}
          onContextMenu={handlePopoverOpen}
        >
          <>
            {" "}
            {flightRemovalType ? <WarningIcon color="secondary" className="cancelled_icon" /> : null}
            <Grid
              container
              className="info"
              wrap="nowrap"
              alignItems="center"
              justifyContent="space-between"
              style={{ marginLeft: flightRemovalType ? 5 : 0 }}
            >
              <Grid item className="origin">
                {originCode}
              </Grid>
              <Grid item className="airline_flight">
                {airlineFlight}
              </Grid>
              <Grid item className="destination">
                {destCode}
              </Grid>
            </Grid>
            {!disableContext && internalFlight && internalFlightData && (
              <ChipContextMenu event={internalFlightData} anchorEl={contextAnchor} setAnchor={setContextAnchor} />
            )}
          </>
        </Grid>
      </Tooltip>
    );
  }
  return <></>;
};
